import React from "react";
import {
    Box,
    Container,
    Typography,
    TextField,
    Button,
    Stack,
    Link,
} from "@mui/material";
import StarIcon from "@mui/icons-material/Star";
import LockIcon from "@mui/icons-material/Lock";
import PeopleIcon from "@mui/icons-material/People";
import { height } from "@mui/system";
import { Homeimages } from "../../assets";
import { useForm, Controller } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { toast } from 'react-toastify';
import { useMutation } from '@tanstack/react-query';
import { contactForm } from "../../api/services";

const schema: yup.ObjectSchema<Omit<LeadData, 'type'>> = yup.object({
    name: yup.string().required('Name is required'),
    email: yup
        .string()
        .email('Invalid email format')
        .required('Email is required'),
    phoneNumber: yup
        .string()
        .matches(
            /^[6-9]\d{9}$/,
            'Phone number must start with 6, 7, 8, or 9 and be 10 digits',
        )
        .required('Phone number is required'),
    message: yup.string().optional(),
    source: yup.string().optional(),
});

// ✅ UPDATED: Added source field to interface
interface LeadData {
    name: string;
    email: string;
    phoneNumber: string;
    message?: string;
    type?: string;
    source?: string;
}

const inputStyle = {
    borderRadius: "12px",
    background: "rgba(255,255,255,0.08)",
    color: "#fff",
    "& input, & textarea": {
        color: "#fff",
        caretColor: "#fff",
        fontSize: "14px",
        lineHeight: "1",
    },
    "& input::placeholder, & textarea::placeholder": {
        color: "rgba(255,255,255,0.6)",
        opacity: 1,
        fontSize: "14px",
    },
    "& fieldset": {
        border: "1px solid rgba(255,255,255,0.2)",
    },
    "&:hover fieldset": {
        border: "1px solid rgba(255,255,255,0.4)",
    },
    "&.Mui-focused": {
        background: "rgba(255,255,255,0.08)",
    },
    "&.Mui-focused fieldset": {
        border: "1px solid #4fc3f7",
    },
};

const PremiumHook = () => {
    const {
        control,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm<LeadData>({
        resolver: yupResolver(schema),
    });

    const mutation = useMutation({
        mutationFn: (newFormData: LeadData) => contactForm(newFormData),
        onSuccess: () => {
            toast.success('Successfully submitted!');
            resetForm();
        },
        onError: (error: any) => {
            toast.error(error?.response?.data?.message || 'Submission failed');
        },
    });

    const resetForm = () => {
        reset({
            name: '',
            email: '',
            phoneNumber: '',
            message: '',
        });
    };

    // ✅ UPDATED: Added source field with "leadgen webpage" value
    const onSubmit = (data: LeadData) => {
        mutation.mutate({ ...data, type: 'contact', source: 'leadgen webpage' });
    };

    return (
        <Box
            sx={{
                position: "relative",
                minHeight: "100vh",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
            }}
        >
            {/* 🌆 BG IMAGE */}
            <Box
                sx={{
                    position: "absolute",
                    inset: 0,
                    backgroundImage: `url("https://dprstorage.b-cdn.net/RW/leadgen/footersec.png")`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    zIndex: -2,
                }}
            />

            {/* 🔵 DARK OVERLAY (LEFT DARK → RIGHT BLUE) */}
            <Box
                sx={{
                    position: "absolute",
                    inset: 0,
                    background: `
            linear-gradient(
              90deg,
              rgba(0,0,0,0.65) 0%,
              rgba(15,99,165,0.65) 100%
            )
          `,
                    zIndex: -1,
                }}
            />

            <Container maxWidth="md">
                {/* 💎 CARD */}
                <Box
                    sx={{
                        mx: "auto",
                        textAlign: "center",
                        px: 5,
                        py: 4,
                        borderRadius: "22px",
                        background: "rgba(120,140,160,0.25)",
                        backdropFilter: "blur(2px)",
                        WebkitBackdropFilter: "blur(2px)",
                        border: "1px solid rgba(255,255,255,0.25)",
                        boxShadow: "0 30px 80px rgba(0,0,0,0.6)",
                    }}
                >
                    <Box
                        component="img"
                        src={Homeimages.rwsignlogo}
                        alt="logo"
                        sx={{
                            width: 60,
                            height: 60,
                            mb: 1,
                            mx: "auto",
                            display: "block",
                            filter: "drop-shadow(0 0 10px rgba(79,195,247,0.6))",
                        }}
                    />
                    {/* TITLE */}
                    <Typography
                        variant="h6"
                        sx={{
                            color: "#fff",
                            fontWeight: 700,
                            fontSize: "32px",
                            mb: 2,
                        }}
                    >
                        Ready to Close Premium Deals?
                    </Typography>

                    {/* INPUTS */}
                    <Box
                        onSubmit={handleSubmit(onSubmit)}
                        component="form"
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 2,
                            maxWidth: "500px",
                            margin: "0 auto",
                        }}
                    >

                        {/* Name */}
                        <Controller
                            name="name"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    placeholder="Enter your full name"
                                    {...field}
                                    fullWidth
                                    InputProps={{ sx: inputStyle }}
                                    error={!!errors.name}
                                    helperText={errors.name?.message}
                                />
                            )}
                        />

                        {/* Email */}
                        <Controller
                            name="email"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    placeholder="Enter your email address"
                                    {...field}
                                    fullWidth
                                    InputProps={{ sx: inputStyle }}
                                    error={!!errors.email}
                                    helperText={errors.email?.message}
                                />
                            )}
                        />

                        {/* PHONE */}
                        <Controller
                            name="phoneNumber"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    placeholder="Enter your phone number"
                                    {...field}
                                    fullWidth
                                    InputProps={{ sx: inputStyle }}
                                    error={!!errors.phoneNumber}
                                    helperText={errors.phoneNumber?.message}
                                />
                            )}
                        />

                        {/* MESSAGE */}
                        <Controller
                            name="message"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    placeholder="Enter your message"
                                    {...field}
                                    multiline
                                    rows={3}
                                    fullWidth
                                    InputProps={{ sx: inputStyle }}
                                />
                            )}
                        />

                        {/* Button */}
                        <Button
                            type="submit"
                            fullWidth
                            sx={{
                                mt: 2,
                                py: 1.6,
                                borderRadius: "50px",
                                fontWeight: 600,
                                fontSize: "1rem",
                                textTransform: "none",
                                background:
                                    "linear-gradient(135deg, #1e88e5, #42a5f5)",
                                color: "#fff",
                                boxShadow: "0 10px 30px rgba(0,0,0,0.4)",
                                transition: "0.3s",
                                "&:hover": {
                                    transform: "translateY(-2px)",
                                    boxShadow: "0 15px 40px rgba(0,0,0,0.5)",
                                },
                            }}
                        >
                            Submit →
                        </Button>

                    </Box>

                    {/* ✅ BOTTOM POINTS (EXACT ALIGNMENT) */}
                    {/* <Stack
                        direction="row"
                        justifyContent="center"
                        alignItems="center"
                        spacing={3}
                        sx={{
                            mt: 3,
                            flexWrap: "wrap",
                            rowGap: 1.5,
                        }}
                    >
                        
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                            {[...Array(5)].map((_, i) => (
                                <StarIcon
                                    key={i}
                                    sx={{ color: "#FFD700", fontSize: 16 }}
                                />
                            ))}
                            <Typography sx={{ color: "#fff", fontSize: 14 }}>
                                4.9 Rating
                            </Typography>
                        </Stack>

                        
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <PeopleIcon sx={{ color: "#fff", fontSize: 16 }} />
                            <Typography sx={{ color: "#fff", fontSize: 14 }}>
                                500+ Clients
                            </Typography>
                        </Stack>

                       
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <LockIcon sx={{ color: "#fff", fontSize: 16 }} />
                            <Typography sx={{ color: "#fff", fontSize: 14 }}>
                                Secure & Confidential
                            </Typography>
                        </Stack>
                    </Stack> */}
                </Box>

                <Box
                    sx={{
                        borderTop: "1px solid rgba(242, 243, 244, 0.66)",
                        mt: 3,
                        pt: 2,
                        textAlign: "center",
                    }}
                >
                    <Typography
                        sx={{
                            color: "#64748b",
                            fontSize: 14,
                        }}
                    >
                        © 2026{" "}
                        <Link
                            href="https://dezignshark.com"
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                                color: "#f5f8f9",
                                textDecoration: "none",
                                fontWeight: 600,
                                "&:hover": {
                                    textDecoration: "underline",
                                },
                            }}
                        >
                            Dezignshark
                        </Link>
                        . All rights reserved.
                    </Typography>
                </Box>
            </Container>

        </Box>
    );
};

export default PremiumHook;